// src/monitoring/pages/Park.jsx - one park: today's curve against expected,
// every inverter, its alarms and its last 30 days.

import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, Grid, Paper, Stack, Table, TableBody, TableCell, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Area, Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { useMonitoring } from "../MonitoringContext";
import { alarmMessage, useMonitoringText } from "../i18n";
import { useLanguage } from "../../context/LanguageContext";
import { fetchParkDetail } from "../services/monitoringApi";
import useMonitoringChartTheme from "../components/chartTheme";
import {
  ReplayBadge, SectionTitle, SeverityChip, SimulatedBadge, StatTile, StatusChip, fmtDate, fmtDateTime, fmtKw, fmtKwh, fmtKwp, fmtNum, fmtPct, fmtTime, relativeColor, useLocale,
} from "../components/primitives";

function localToday(tz) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz || "Europe/Athens", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

export default function Park() {
  const { sourceId } = useParams();
  const m = useMonitoring();
  const t = useMonitoringText();
  const { language } = useLanguage();
  const locale = useLocale();
  const chartTheme = useMonitoringChartTheme();
  const summary = m.parks.find((p) => p.id === sourceId);
  const tz = summary?.plant?.timezone || "Europe/Athens";
  const [day, setDay] = useState(() => localToday(tz));
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sourceId || !m.accessToken) return undefined;
    const controller = new AbortController();
    setLoading(true); setError(null);
    fetchParkDetail(sourceId, day === localToday(tz) ? undefined : day, { accessToken: m.accessToken, signal: controller.signal })
      .then((d) => { if (!controller.signal.aborted) setDetail(d); })
      .catch((err) => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [sourceId, day, tz, m.accessToken, m.portfolio]);

  const series = useMemo(() => (detail?.series || []).map((s) => ({
    ts: s.ts, time: fmtTime(s.ts, locale, tz), actual: s.p_ac_kw == null ? null : Math.round(s.p_ac_kw), expected: s.p_ac_kw_expected == null ? null : Math.round(s.p_ac_kw_expected),
  })).filter((s) => s.actual !== null || (s.expected !== null && s.expected > 0)), [detail, locale, tz]);
  const basis = useMemo(() => {
    const values = detail?.series || [];
    if (values.some((s) => s.basis === "project_forecast")) return "project_forecast";
    if (values.some((s) => s.basis === "imported")) return "imported";
    if (values.some((s) => s.basis === "clearsky")) return "clearsky";
    return "weather";
  }, [detail]);
  const daily = useMemo(() => (detail?.daily || []).map((d) => ({
    day: fmtDate(d.day, locale), pr: d.pr == null ? null : Math.round(d.pr * 100), availability: d.availability == null ? null : Math.round(d.availability * 100), e: d.e_kwh,
  })), [detail, locale]);

  if (!summary && m.loaded) {
    return <Alert severity="warning" action={<Button component={RouterLink} to="/monitoring" color="inherit" size="small">{t("park.back")}</Button>}>{t("park.notFound")}</Alert>;
  }
  const park = detail?.park || summary;
  const plant = park?.plant || {};
  const pendingVerification = (park?.verification_pending?.critical || 0) + (park?.verification_pending?.warning || 0) + (park?.verification_pending?.info || 0);

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Button component={RouterLink} to="/monitoring" startIcon={<ArrowBackIcon />} size="small">{t("park.back")}</Button>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{park?.name}</Typography>
        <StatusChip status={park?.status} />
        {park?.simulated && <SimulatedBadge />}
        {park?.replay && <ReplayBadge />}
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {fmtKwp(plant.dc_kwp, locale)} / {fmtKw(plant.ac_kw, locale)} · {plant.mounting === "tracker_1axis" ? t("form.mounting.tracker") : t("form.mounting.fixed")}
          {plant.tilt_deg != null && plant.mounting !== "tracker_1axis" ? ` ${fmtNum(plant.tilt_deg, 0, locale)}° / ${fmtNum(plant.azimuth_deg, 0, locale)}°` : ""}
          {plant.inverter_model ? ` · ${plant.inverter_model}` : ""}{plant.module_model ? ` · ${plant.module_model}` : ""}
        </Typography>
      </Stack>

      {pendingVerification > 0 && <Alert severity="warning">{t("park.pendingVerification")}</Alert>}

      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatTile label={t("portfolio.livePower")} value={fmtKw(park?.live?.p_ac_kw, locale)}
            sub={park?.live?.p_ac_kw_expected != null ? `${fmtKw(park.live.p_ac_kw_expected, locale)} ${t("portfolio.expectedNow")}` : undefined} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatTile label={t("portfolio.energyToday")} value={fmtKwh(park?.live?.e_today_kwh, locale)}
            sub={park?.live?.e_expected_today_kwh ? `${fmtKwh(park.live.e_expected_today_kwh, locale)} ${t("portfolio.expected").toLowerCase()}` : undefined} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatTile label={t("portfolio.prToday")} value={fmtPct(park?.live?.pr_today, 0, locale)}
            accent={park?.live?.pr_today == null ? undefined : park.live.pr_today >= 0.85 ? "success.main" : park.live.pr_today >= 0.7 ? "warning.main" : "error.main"}
            sub={plant.pr_target ? `${t("reports.col.prTarget")}: ${fmtPct(plant.pr_target, 0, locale)}` : undefined} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatTile label={t("portfolio.inverters")} value={`${park?.inverters?.reporting ?? "—"} / ${park?.inverters?.total ?? "—"}`}
            sub={`${park?.inverters?.producing ?? "—"} ${t("portfolio.producing")} · ${t("portfolio.col.lastReading").toLowerCase()} ${fmtTime(park?.live?.last_reading_at, locale, tz)}`} />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <SectionTitle title={t("park.powerVsExpected")} hint={t(`park.basis.${basis}`)}
          action={<TextField type="date" size="small" label={t("park.day")} value={day} onChange={(e) => e.target.value && setDay(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />} />
        {loading && !detail ? <CircularProgress size={22} /> : error ? <Alert severity="error">{error}</Alert>
          : series.length === 0 ? <Typography color="text.secondary">{t("park.noSeries")}</Typography> : (
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer>
                <ComposedChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="time" tick={chartTheme.axisTick} axisLine={chartTheme.axisLine} tickLine={chartTheme.tickLine} interval="preserveStartEnd" minTickGap={40} />
                  <YAxis tick={chartTheme.axisTick} axisLine={chartTheme.axisLine} tickLine={chartTheme.tickLine} unit=" kW" width={72} />
                  <ChartTooltip {...chartTheme.tooltip} formatter={(v) => `${fmtNum(v, 0, locale)} kW`} />
                  <Legend {...chartTheme.legend} />
                  <Area type="monotone" dataKey="expected" name={t("portfolio.expected")} stroke={chartTheme.expectedStroke} fill={chartTheme.expectedFill} strokeDasharray="4 3" connectNulls dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="actual" name={t("portfolio.actual")} stroke="#1976d2" strokeWidth={2.2} dot={false} connectNulls={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          )}
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <SectionTitle title={`${t("park.inverters")} (${detail?.inverters?.length ?? park?.inverters?.total ?? 0})`} hint={t("park.invertersHint")}
              action={detail?.median_specific_yield_today != null ? <Typography variant="caption" color="text.secondary">{t("park.median")}: {fmtNum(detail.median_specific_yield_today, 2, locale)} kWh/kWp</Typography> : null} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1 }}>
              {(detail?.inverters || []).map((inv) => (
                <Tooltip key={inv.id} arrow title={
                  <Box>
                    <div>{inv.model || inv.name}</div>
                    <div>{t("portfolio.col.power")}: {fmtKw(inv.p_ac_kw, locale)} / {fmtKw(inv.ac_kw, locale)}</div>
                    <div>{t("portfolio.col.today")}: {fmtKwh(inv.e_day_kwh, locale)}</div>
                    {inv.mppt && <div>{t("park.mppt")}: {Object.entries(inv.mppt).map(([k, v]) => `${k}: ${fmtNum(v.v, 0, locale)} V / ${fmtNum(v.i, 1, locale)} A`).join(" · ")}</div>}
                    <div>{t("park.status")}: {inv.status_code || "—"} · {fmtTime(inv.ts, locale, tz)}</div>
                  </Box>
                }>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 1.5, borderLeft: "5px solid", borderLeftColor: relativeColor(inv.relative_to_median) }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{inv.name}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{fmtKw(inv.p_ac_kw, locale)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {inv.relative_to_median != null ? `${fmtPct(inv.relative_to_median, 0, locale)} ${t("park.median")}` : t("status.no_data")}
                    </Typography>
                  </Paper>
                </Tooltip>
              ))}
            </Box>
            {(detail?.devices || []).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t("park.devices")}</Typography>
                <Table size="small">
                  <TableBody>
                    {detail.devices.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{t(`park.kind.${d.kind}`)}</TableCell>
                        <TableCell>{d.name}{d.model ? ` · ${d.model}` : ""}</TableCell>
                        <TableCell align="right">
                          {d.kind === "weather" ? `${fmtNum(d.irradiance_wm2, 0, locale)} W/m² · ${fmtNum(d.temp_c, 1, locale)} °C` : `${fmtKw(d.p_ac_kw, locale)} · ${fmtKwh(d.e_day_kwh, locale)}`}
                        </TableCell>
                        <TableCell align="right">{fmtTime(d.ts, locale, tz)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <SectionTitle title={t("park.openAlarms")} action={<Button size="small" component={RouterLink} to={`/monitoring/alarms?park=${sourceId}`}>{t("alarms.title")}</Button>} />
            {(detail?.alarms_open || []).length === 0 ? <Typography color="text.secondary">{t("park.noOpenAlarms")}</Typography> : (
              <Stack spacing={1}>
                {detail.alarms_open.map((a) => (
                  <Paper key={a.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SeverityChip severity={a.severity} />
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{t(`rule.${a.rule}`)}</Typography>
                      <Typography variant="caption" color="text.secondary">{fmtDateTime(a.opened_at, locale, tz)}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{alarmMessage(language, a)}</Typography>
                  </Paper>
                ))}
              </Stack>
            )}
            {(detail?.alarms_recent || []).filter((a) => a.closed_at).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t("park.recentAlarms")}</Typography>
                <Table size="small">
                  <TableBody>
                    {detail.alarms_recent.filter((a) => a.closed_at).slice(0, 8).map((a) => (
                      <TableRow key={a.id}>
                        <TableCell sx={{ width: 90 }}><SeverityChip severity={a.severity} /></TableCell>
                        <TableCell>{alarmMessage(language, a)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>{fmtDateTime(a.opened_at, locale, tz)} → {fmtDateTime(a.closed_at, locale, tz)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {daily.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <SectionTitle title={t("park.daily")} />
          <Box sx={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={chartTheme.axisTick} axisLine={chartTheme.axisLine} tickLine={chartTheme.tickLine} interval="preserveStartEnd" />
                <YAxis tick={chartTheme.axisTick} axisLine={chartTheme.axisLine} tickLine={chartTheme.tickLine} unit=" %" domain={[0, 110]} width={60} />
                <ChartTooltip {...chartTheme.tooltip} formatter={(v, name) => [`${fmtNum(v, 0, locale)} %`, name]} />
                <Legend {...chartTheme.legend} />
                <Bar dataKey="pr" name={t("park.pr")} fill="#1976d2" radius={[3, 3, 0, 0]} />
                <Bar dataKey="availability" name={t("park.availability")} fill="#80cbc4" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
    </Stack>
  );
}
